import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IItemCreationSubsectionInputProps, ItemCreationToolTip } from '~/Components';
import { useStyles } from './Merchant.style';
import { IMerchant, FormField, InputType, FormGroups } from '~/Models/DTO/Merchant.models';
import { useUser } from '~/Hooks';
import { Checkbox, useMediaQuery, useTheme } from '@mui/material';
import { TextField } from '~/UIKit';
import cn from 'classnames';

interface IUseFilterMerchantFields {
  fields: FormField[];
  groups: FormGroups[];
  values: IMerchant;
  customField?: string;
  setCustomField?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setBillable?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setConsent?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isModal?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  create?: boolean;
  rateOptions?: any[];
}

const discardEvent = (event: React.ChangeEvent<HTMLInputElement>) => event.preventDefault();
export const useFieldsByGroup = ({
  fields,
  values,
  groups,
  setBillable = () => {},
  setConsent = () => {},
  isModal = false,
  customField = '',
  setCustomField = () => {},
  containerRef,
  create = false,
}: IUseFilterMerchantFields) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { DANGEROUSLY_isSuperAdmin: isAdmin } = useUser();
  const DANGEROUSLY_isSuperAdmin = isAdmin || isModal;
  const { classes } = useStyles();
  const labelMaker = useCallback(
    (label: string, required?: boolean, optional?: boolean) => (
      <label className={classes.label}>
        {`${t(label)}${required ? '*' : ''}`}
        {!required && optional && (
          <span className={classes.optional}>{` (${t('app.optional')})`}</span>
        )}
      </label>
    ),
    [],
  );

  const formatOptions = useCallback(
    (options: any[]) => options.map(({ label, value }) => ({ value, label: t(label) })),
    [],
  );

  const getGroupLabel = useCallback(
    (groupId: number) => {
      const groupRecord = groups.find(({ value }) => value == groupId) || {
        label: '',
        tooltip: null,
      };
      return !!groupRecord?.tooltip ? (
        <div className={classes.groupLabel}>
          {t(groupRecord?.label)}{' '}
          <span className={classes.groupLabelIcon}>
            <ItemCreationToolTip
              {...{
                title: t(groupRecord?.tooltip),
                placement: isMobile ? 'auto' : 'auto-start',
              }}
              containerRef={containerRef}
            />
          </span>
        </div>
      ) : (
        <label className={classes.groupLabel}>{t(groupRecord.label)}</label>
      );
    },
    [groups],
  );
  const getFieldsById = useCallback(
    (groupId: number) => {
      const infoSection: IItemCreationSubsectionInputProps<Partial<IMerchant>>[] = [];
      if (groupId === 0 && !isModal) {
        infoSection.push({
          name: 'billable',
          customElement: (
            <div className={classes.billable}>
              <Checkbox
                name="billable"
                onChange={setBillable}
                className={classes.consentBox}
                checked={!!values?.billable}
                disabled={!DANGEROUSLY_isSuperAdmin}
              />
              <span className={classes.billableText}>{labelMaker('merchantForm.billable')}</span>
            </div>
          ),
        });
      }
      fields
        .filter(({ groupBy }: FormField) => groupBy == groupId)
        .filter(({ groupBy }: FormField) =>
          groupBy === groupId && groupId === 3 && isModal ? false : true,
        )
        .forEach(({ name, label, type, required, options }: FormField) => {
          const isEditDefaultRate = name === 'defaultSinglePayoutRate' && !create;
          const fieldOptions = isEditDefaultRate
            ? options
            : options?.length
            ? formatOptions(options)
            : undefined;
          const additionalOptions = {} as any;
          if (type === 'number') {
            additionalOptions['InputProps'] = {
              inputProps: {
                min: 0,
                onKeyDown: (event: KeyboardEvent) =>
                  ['e', '.', '-', '+'].includes(event.key) && event.preventDefault(),
                onPaste: discardEvent,
              },
            };
          }
          additionalOptions['InputProps'] = {
            ...additionalOptions['InputProps'],
            inputProps: {
              ...additionalOptions['InputProps']?.inputProps,
            },
          };
          const field = {
            name: `${name}`,
            label: labelMaker(label, required as boolean, !required),
            placeholder: t(label),
            type: InputType[type],
            options: fieldOptions,
            required,
            disabled: [3, 4, 5].includes(groupId) && !DANGEROUSLY_isSuperAdmin,
            ...additionalOptions,
          } as IItemCreationSubsectionInputProps<Partial<IMerchant>>;
          infoSection.push(field);
        });

      if (groupId === 5) {
        infoSection.push({
          customElement: (
            <div
              className={cn(classes.note, {
                [classes.disabled]: !DANGEROUSLY_isSuperAdmin,
              })}
            >
              <label className={classes.label}>{t('merchantForm.note.label')}</label>
              <TextField
                name="customField"
                className={classes.customField}
                newDesign
                defaultValue={customField}
                multiline
                onChange={setCustomField}
              />
            </div>
          ),
        });
      }
      if ((isModal && groupId == 2) || (!isModal && groupId == 5)) {
        const field = {
          name: 'consent',
          customElement: (
            <div className={classes.consent}>
              <Checkbox
                name="consent"
                onChange={setConsent}
                className={classes.consentBox}
                checked={!!values?.consent}
              />
              <span className={classes.noteText}>
                {t('merchantForm.consent', {
                  value: t(`merchantForm.consent.companyname.${values?.country}`),
                })}
              </span>
            </div>
          ),
        } as IItemCreationSubsectionInputProps<Partial<IMerchant>>;
        infoSection.push(field);
      }
      return infoSection;
    },
    [fields, DANGEROUSLY_isSuperAdmin, values?.country, values?.billable, values?.consent],
  );
  return { getFieldsById, getGroupLabel };
};
